import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartPlus, faMinus, faPlus, faShoppingCart, faSearch } from '@fortawesome/free-solid-svg-icons';
import AuthenticateService from './AuthenticateService';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';
import PreLoader from './extra/PreLoader';
import { cartStorage } from './services/cartStorage';

const money = (n) => `₦${(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Store = () => {
  const { http, user, logout } = AuthenticateService();

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(0); // 0 = all
  const [search, setSearch] = useState('');
  const [qty, setQty] = useState({}); // productId -> chosen quantity before adding
  const [cartCount, setCartCount] = useState(cartStorage.count());

  useEffect(() => {
    document.title = 'Store';
    loadCategories();
    loadProducts(0);
    const unsubscribe = cartStorage.subscribe(() => setCartCount(cartStorage.count()));
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCategories = async () => {
    try {
      const res = await http.post('/ecommerce/category', {});
      if (res.data.status === true) {
        setCategories(res.data.data || []);
      }
    } catch (error) {
      // categories are a filter convenience; a failure here should not block browsing products
      console.error('Could not load categories:', error);
    }
  };

  const loadProducts = async (categoryId) => {
    setLoading(true);
    try {
      const res = await http.post('/ecommerce/items', { page: 1, category: categoryId });
      if (res.data.status === true) {
        setProducts(res.data.product || []);
        cartStorage.sync(res.data.product || []); // drop cart lines for products that vanished or sold out
      } else {
        toast.error(res.data.message || 'Could not load products');
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        logout();
      } else {
        toast.error('Could not load products, please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  const selectCategory = (id) => {
    setActiveCategory(id);
    loadProducts(id);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [products, search]);

  const qtyFor = (id) => qty[id] || 1;
  const setQtyFor = (id, value, max) => {
    let v = parseInt(value, 10);
    if (!Number.isFinite(v) || v < 1) v = 1;
    if (max > 0 && v > max) v = max;
    setQty((prev) => ({ ...prev, [id]: v }));
  };

  const addToCart = (product) => {
    const error = cartStorage.add(product, qtyFor(product.id));
    if (error) {
      toast.warning(error);
    } else {
      toast.success(`${product.name} added to cart`);
      setQty((prev) => ({ ...prev, [product.id]: 1 }));
    }
  };

  return (
    <>
      {loading && products.length === 0 ? (
        <PreLoader />
      ) : (
        <>
          <Sidebar />
          <Header user={user} />

          <div className="content">
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
              <h4 className="fs-1 mb-2">Store</h4>
              <Link to="/cart" className="btn btn-primary position-relative mb-2">
                <FontAwesomeIcon icon={faShoppingCart} className="me-2" />
                View Cart
                {cartCount > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    {cartCount}
                    <span className="visually-hidden">items in cart</span>
                  </span>
                )}
              </Link>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <FontAwesomeIcon icon={faSearch} />
                  </span>
                  <input
                    className="form-control"
                    placeholder="Search products"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {categories.length > 0 && (
              <div className="d-flex overflow-auto pb-2 mb-3" style={{ gap: '8px' }}>
                <span
                  onClick={() => selectCategory(0)}
                  className={`btn btn-sm rounded-pill ${activeCategory === 0 ? 'btn-primary' : 'btn-outline-primary'}`}
                  style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  All
                </span>
                {categories.map((c) => (
                  <span
                    key={c.id}
                    onClick={() => selectCategory(c.id)}
                    className={`btn btn-sm rounded-pill ${activeCategory === c.id ? 'btn-primary' : 'btn-outline-primary'}`}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {c.name}
                  </span>
                ))}
              </div>
            )}

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="alert alert-info">No products found.</div>
            ) : (
              <div className="row g-4">
                {filtered.map((product) => {
                  const hasDiscount = parseFloat(product.actual_amount) > parseFloat(product.amount);
                  const discountPct = hasDiscount
                    ? Math.round(((product.actual_amount - product.amount) / product.actual_amount) * 100)
                    : 0;
                  const outOfStock = (parseInt(product.quantity, 10) || 0) < 1;
                  return (
                    <div key={product.id} className="col-12 col-sm-6 col-lg-4 col-xxl-3">
                      <div className="card h-100">
                        <div className="position-relative">
                          <img
                            src={product.cover_img}
                            alt={product.name}
                            className="card-img-top"
                            style={{ height: 170, objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/logo.png';
                            }}
                          />
                          {hasDiscount && (
                            <span className="badge bg-danger position-absolute top-0 start-0 m-2">
                              -{discountPct}%
                            </span>
                          )}
                          {outOfStock && (
                            <span className="badge bg-secondary position-absolute top-0 end-0 m-2">
                              Out of stock
                            </span>
                          )}
                        </div>
                        <div className="card-body d-flex flex-column">
                          <h6 className="mb-1 text-truncate" title={product.name}>{product.name}</h6>
                          {product.category?.name && (
                            <div className="text-500 fs--1 mb-2">{product.category.name}</div>
                          )}
                          <div className="mb-2">
                            {hasDiscount && (
                              <span className="text-500 text-decoration-line-through me-2 fs--1">
                                {money(product.actual_amount)}
                              </span>
                            )}
                            <span className="fw-bold text-primary">{money(product.amount)}</span>
                          </div>
                          <div className="mt-auto">
                            {!outOfStock && (
                              <div className="d-flex align-items-center mb-2" style={{ gap: '8px' }}>
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => setQtyFor(product.id, qtyFor(product.id) - 1, product.quantity)}
                                >
                                  <FontAwesomeIcon icon={faMinus} />
                                </button>
                                <input
                                  type="number"
                                  className="form-control form-control-sm text-center"
                                  style={{ width: 56 }}
                                  min={1}
                                  max={product.quantity}
                                  value={qtyFor(product.id)}
                                  onChange={(e) => setQtyFor(product.id, e.target.value, product.quantity)}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => setQtyFor(product.id, qtyFor(product.id) + 1, product.quantity)}
                                >
                                  <FontAwesomeIcon icon={faPlus} />
                                </button>
                              </div>
                            )}
                            <button
                              type="button"
                              className="btn btn-primary w-100"
                              disabled={outOfStock}
                              onClick={() => addToCart(product)}
                            >
                              <FontAwesomeIcon icon={faCartPlus} className="me-2" />
                              {outOfStock ? 'Unavailable' : 'Add to Cart'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <Footer />
          </div>

          <ToastContainer />
        </>
      )}
    </>
  );
};

export default Store;
